/**
 * The Canvas2D fallback.
 *
 * Slower is fine; different is not (M0-05: "Canvas2D fallback renders
 * identically"). A fallback nobody can trust to look the same is a fallback
 * that hides bugs on exactly the hardware least able to report them.
 *
 * The one real problem Canvas2D has here is tinting: the atlas is a white
 * stencil, and `drawImage` cannot recolour as it draws. Doing it per glyph
 * with `globalCompositeOperation` would mean two composite passes per cell,
 * thousands of times a frame. Instead each colour gets a pre-tinted copy of
 * the whole atlas, made once and cached — the palette is small and deliberately
 * so (design spec §3.1), so the cache stays small too.
 */

import {
  atlasCell,
  type GlyphAtlas,
  type Renderer,
  type Sprite,
  type TerrainFrame,
} from './renderer.js';
import { css } from './palette.js';

export class CanvasRenderer implements Renderer {
  readonly kind = 'canvas2d' as const;

  private readonly context: CanvasRenderingContext2D;
  private readonly atlas: GlyphAtlas;

  /** One pre-tinted atlas per colour ever asked for. */
  private readonly tinted = new Map<number, HTMLCanvasElement>();

  private width = 0;
  private height = 0;

  constructor(canvas: HTMLCanvasElement, atlas: GlyphAtlas) {
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('Canvas2D is not available');

    this.context = context;
    this.atlas = atlas;
    this.width = canvas.width;
    this.height = canvas.height;

    // Match the GL path's NEAREST sampling. Smoothing a bitmap font is the one
    // thing that would make the two renderers visibly disagree.
    context.imageSmoothingEnabled = false;
  }

  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    this.context.imageSmoothingEnabled = false;
  }

  draw(terrain: TerrainFrame, sprites: readonly Sprite[]): void {
    const ctx = this.context;
    const { cellWidth, cellHeight } = this.atlas;

    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, this.width, this.height);

    // Backgrounds first, in runs of the same colour. Terrain is mostly flat
    // fields, so this collapses a few thousand fillRects into a few hundred.
    for (let row = 0; row < terrain.rows; row += 1) {
      const y = terrain.offsetY + row * cellHeight;
      let run = 0;
      let runColour = -1;

      const flush = (endColumn: number) => {
        if (run === 0 || runColour < 0) return;
        ctx.fillStyle = css(runColour);
        ctx.fillRect(
          terrain.offsetX + (endColumn - run) * cellWidth,
          y,
          run * cellWidth,
          cellHeight,
        );
        run = 0;
      };

      for (let column = 0; column < terrain.columns; column += 1) {
        const colour = terrain.bg[row * terrain.columns + column] ?? 0;
        if (colour !== runColour) {
          flush(column);
          runColour = colour;
        }
        run += 1;
      }
      flush(terrain.columns);
    }

    for (let row = 0; row < terrain.rows; row += 1) {
      for (let column = 0; column < terrain.columns; column += 1) {
        const index = row * terrain.columns + column;
        const glyph = terrain.glyphs[index] ?? 0;
        if (glyph === 0) continue;

        this.blit(
          glyph,
          terrain.fg[index] ?? 0,
          terrain.offsetX + column * cellWidth,
          terrain.offsetY + row * cellHeight,
        );
      }
    }

    for (const sprite of sprites) this.blit(sprite.glyph, sprite.fg, sprite.x, sprite.y);
  }

  private blit(glyph: number, colour: number, x: number, y: number): void {
    const source = atlasCell(this.atlas, glyph);
    const { cellWidth, cellHeight } = this.atlas;

    this.context.drawImage(
      this.tintedAtlas(colour),
      source.x,
      source.y,
      cellWidth,
      cellHeight,
      x,
      y,
      cellWidth,
      cellHeight,
    );
  }

  /** The atlas, recoloured. Built once per colour and kept. */
  private tintedAtlas(colour: number): HTMLCanvasElement {
    const cached = this.tinted.get(colour);
    if (cached) return cached;

    const width = this.atlas.columns * this.atlas.cellWidth;
    const height = this.atlas.rows * this.atlas.cellHeight;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('could not build a tinted atlas');

    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(this.atlas.image as CanvasImageSource, 0, 0);

    // `source-in` keeps the incoming fill only where the atlas already has
    // ink, which recolours the glyphs and leaves the gaps transparent.
    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = css(colour);
    ctx.fillRect(0, 0, width, height);

    this.tinted.set(colour, canvas);
    return canvas;
  }

  dispose(): void {
    this.tinted.clear();
  }
}
